/*
 * Copyright 2017-present Evernote Corporation. All rights reserved.
 */

import cloneDeep from 'lodash/cloneDeep';

/**
 * A homogeneous collection that maps keys to values, in which each key can map to
 * multiple values. It also preserves the uniqueness of its values.
 *
 * The implementation keeps two mappings. One from each key to multiple corresponding
 * values stored in a Set. The other one from each value back to corresponding key.
 * Thus, runtime complexity of MultiBiMap#set is amortized O(1).
 *
 * MultiBiMap does not support undefined or null as key or value because of flow type
 * system. Undefined and null are considered optional type instead of their
 * corresponding types.
 */
type Indexable = string | number;
export default class MultiBiMap<K extends Indexable, V extends Indexable> {
  forwardMap: Map<K, Set<V>>;
  inwardMap: Map<V, K>;

  /**
   * Creates a new instance of MultiBiMap that has mappings from the argument object.
   * The argument should preferrably be an instance of MultiBiMap or an object that has
   * similar internal structure of MultiBiMap. The new instance is a deep copy of the
   * original MultiBiMap or object.
   *
   * @param obj - An object that either is an instance of MultiBiMap or must have all the internal mappings.
   * @param obj.forwardMap - Instance of Map, mapping keys to multiple values;
   * @param obj.inwardMap - Instance of Map, mapping values to keys.
   * @return A new instance of MultiBiMap.
   */
  static createFromSameInstance<K extends Indexable, V extends Indexable>(obj?: {
    forwardMap: Map<K, Set<V>>;
    inwardMap: Map<V, K>;
  }): MultiBiMap<K, V> {
    if (obj == null) {
      return MultiBiMap.createEmptyMap();
    }
    const forwardMap: Map<K, Set<V>> = obj.forwardMap || new Map();
    const inwardMap: Map<V, K> = obj.inwardMap || new Map();
    return new MultiBiMap(cloneDeep(forwardMap), cloneDeep(inwardMap));
  }

  static createEmptyMap<K extends Indexable, V extends Indexable>(): MultiBiMap<K, V> {
    return new MultiBiMap();
  }

  /**
   * Creates a new instance of MultiBiMap from an object with one or more array properties.
   * The object's property names are converted to keys for the new map,
   * and the elements of the object's property values arrays are converted to values for their
   * corresponding keys.
   * Ex.  createFromObject({'foo':['b', 'a', 'r']})
   * will create a MultiBiMap with one Key ('foo'), and 3 values ('b', 'a', and 'r') assigned
   * to that key.
   */
  static createFromObject<K extends Indexable, V extends Indexable>(obj: {
    [key: string]: V[];
  }): MultiBiMap<K, V> {
    const map = new MultiBiMap();
    Object.keys(obj).forEach(k => {
      obj[k].forEach(v => {
        map.set(k, v);
      });
    });
    return map as MultiBiMap<K, V>;
  }

  /**
   * Stores a key-value pair. If there already is a value, the method will remove the
   * existing mapping for that value before storing the new one.
   *
   * @param key - Key.
   * @param value  - Value.
   */
  set(key: K, value: V): void {
    if (!this.isValueStorable(key) || !this.isValueStorable(value)) {
      throw new TypeError('Invalid value to use as key or value');
    }

    const existingKey = this.getKey(value);
    if (existingKey != null) {
      const oldValues = this.getValuesAsSet(existingKey);
      if (oldValues != null) {
        oldValues.delete(value);
        if (oldValues.size === 0) {
          this.removeKey(existingKey);
        }
      }
    }

    const existingValues = this.getValuesAsSet(key);
    if (existingValues != null) {
      existingValues.add(value);
    } else {
      this.forwardMap.set(key, new Set([value]));
    }
    this.inwardMap.set(value, key);
  }

  /**
   * Returns an array of values corresponding to the given key, if exists. Otherwise,
   * return null.
   *
   * @param key - Key.
   * @return Optional array of values.
   */
  getValuesAsArray(key: K): V[] | null {
    const values = this.getValuesAsSet(key);
    return values ? Array.from(values) : null;
  }

  /**
   * Returns the key corresponding to the given value, if exists.
   * Otherwise returns undefined if there is no corresponding key for the value.
   *
   * @param value - Optional value.
   * @return Optional key.
   */
  getKey(value: V): K | null | undefined {
    return this.inwardMap.get(value);
  }

  /**
   * Determines whether a mapping for the given key exists in this map.
   *
   * @param key - A key.
   * @return Returns true if such a mapping exists or false otherwise.
   */
  hasKey(key: K): boolean {
    return this.forwardMap.has(key);
  }

  /**
   * Determines whether a mapping for the given value exists in this map.
   *
   * @param value - Optional value.
   * @return Returns true if such mapping exists or false otherwise.
   */
  hasValue(value: V): boolean {
    return this.inwardMap.has(value);
  }

  /**
   * Removes the mapping for the given key in this map.
   *
   * @param key - A key.
   */
  removeKey(key: K): void {
    const removedValues = this.getValuesAsSet(key);
    if (removedValues != null) {
      this.forwardMap.delete(key);
      removedValues.forEach(value => this.inwardMap.delete(value));
    }
  }

  /**
   * Removes the mapping for the given values in this map.
   *
   * @param value - A value.
   */
  removeValue(value: V): void {
    const key = this.getKey(value);
    if (key != null) {
      this.inwardMap.delete(value);
      const values = this.getValuesAsSet(key);
      if (values != null) {
        values.delete(value);
        if (values.size === 0) {
          this.removeKey(key);
        }
      }
    }
  }

  /**
   * Returns a deep copy of the map.
   *
   * @return A deep copy of the map.
   */
  clone(): MultiBiMap<K, V> {
    return MultiBiMap.createFromSameInstance(cloneDeep(this));
  }

  /**
   * Constructs a new instance of MultiBiMap. This constructor is private and should
   * not be used outside of MultiBiMap class.
   *
   * @private
   * @param forwardMap - A mapping from key to multiple values.
   * @param inwardMap  - A mapping from value to key.
   */
  constructor(forwardMap: Map<K, Set<V>> = new Map(), inwardMap: Map<V, K> = new Map()) {
    this.forwardMap = forwardMap;
    this.inwardMap = inwardMap;
  }

  /**
   * Returns a direct reference to the Set that stores values of the given key, if exists.
   * Otherwise returns null if key is null or undefined.
   * It also returns undefined if there is no mapping for the key.
   *
   * This method should only be call internally as it exposes the internal structure of
   * MultiBiMap and direct reference to that internal structure.
   *
   * If this method becomes public, then the returned value must not be a direct
   * reference to the internal data structures.
   *
   * @private
   * @param key - Optional key.
   * @return Optional Set of values.
   */
  getValuesAsSet(key: K): Set<V> | null | undefined {
    return this.forwardMap.get(key);
  }

  /**
   * Determines whether the given value can be stored as key or value.
   *
   * @private
   * @param v - Any value.
   * @return Returns true if the given value is storable as key or value and
   * false otherwise.
   */
  isValueStorable(v: any): boolean {
    return v != null;
  }

  getForwardObject(): { [key: string]: V[] } {
    const obj: { [key: string]: V[] } = {};
    this.forwardMap.forEach((value, key) => {
      obj[key] = Array.from(value);
    });
    return obj;
  }

  getInwardObject(): { [key: string]: K } {
    const obj: { [key: string]: K } = {};
    this.inwardMap.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }
}
